/* -------------------------------------------------------------------------- */
/*                               Validate Email                               */

import { isValidName, startsWithNumber, trimString } from "./utilities";

/* -------------------------------------------------------------------------- */
export type EmailValidationResult = {
  isValid: boolean;
  message: string;
};

export function validateEmail(email: string): EmailValidationResult {
  email = trimString(email);

  if (email === "") {
    return {
      isValid: false,
      message: "Email cannot be empty.",
    };
  }
  const specialCharRegex = /[!#$%^&*()"'`~<>/?]/;
  const numberRegex = /^[0-9]/;

  if (!email.includes("@")) {
    return {
      isValid: false,
      message: "Email must include '@' symbol",
    };
  }

  const [localPart] = email.split("@");
  if (specialCharRegex.test(localPart)) {
    return {
      isValid: false,
      message:
        "Email username must not contain special characters like !, #, $, etc",
    };
  }

  if (numberRegex.test(email)) {
    return {
      isValid: false,
      message: "Email must not start with a number.",
    };
  }

  return {
    isValid: true,
    message: "Email is valid",
  };
}

export type NameValidationResult = {
  isValid: boolean;
  message: string;
};

export function validateName(name: string): NameValidationResult {
  name = trimString(name);

  if (name.length === 0) {
    return {
      isValid: false,
      message: "Name cannot be empty",
    };
  }

  if (name.length > 50) {
    return {
      isValid: false,
      message: "Cannot be > 50 characters",
    };
  }

  if (!isValidName(name, false)) {
    return {
      isValid: false,
      message: "Should contain alphabets only!",
    };
  }

  return {
    isValid: true,
    message: "Name passed all validations",
  };
}

/* -------------------------------------------------------------------------- */
/*                               Validate ORG Name                            */
/* -------------------------------------------------------------------------- */
export type OrgNameValidationResult = {
  isValid: boolean;
  message: string;
};
export function validateOrgName(name: string): OrgNameValidationResult {
  name = trimString(name);

  if (name.length === 0) {
    return {
      isValid: false,
      message: "Business Name cannot be empty", // Business name is treated as Org Name
    };
  }

  if (startsWithNumber(name)) {
    return {
      isValid: false,
      message: "Business name should not start with number",
    };
  }

  // if (!isValidName(name, true)) {
  //   return {
  //     isValid: false,
  //     message: "Should contain alphabets only!",
  //   };
  // }

  if (name.length < 3) {
    return {
      isValid: false,
      message: "Atleast 3 characters long",
    };
  }

  return {
    isValid: true,
    message: "Business name passed all validations",
  };
}

/* -------------------------------------------------------------------------- */
/*                        Validate Indian phone number                        */
/* -------------------------------------------------------------------------- */
export function validatePhoneNumber(phoneNumber: string): boolean {
  return extractIndianNationalNumber(phoneNumber) !== "";
}

export function isIndiaCountryCode(countryCode?: string | null): boolean {
  const cc = String(countryCode ?? "")
    .trim()
    .toUpperCase();
  return cc === "" || cc === "IN";
}

/** True when the picker has no national number (empty or dial-code only like "91" / "+91"). */
export function isBlankIndiaPhone(phone?: string | null): boolean {
  const digits = String(phone ?? "").replace(/\D/g, "");
  return digits === "" || digits === "91";
}

function collapseDuplicateIndiaCodeInPhoneInput(digits: string): string {
  // Picker already has 91; user typed another 91 at the start of the national field.
  // e.g. 9193848378 → 919193848378. Collapse to 91 + remaining digits so validation can fail on short input.
  // Skip 91917… — valid nationals like 9176543210 become 919176543210 (starts with 91917).
  if (
    digits.startsWith("91") &&
    digits.length === 12 &&
    digits.startsWith("9191") &&
    !digits.startsWith("91917")
  ) {
    return `91${digits.slice(4)}`;
  }
  return digits;
}

function parseIndianNationalDigits(
  digits: string,
  options?: { countryCodeAlreadyPrefixed?: boolean }
): string {
  if (!digits || digits === "91") {
    return "";
  }

  if (options?.countryCodeAlreadyPrefixed) {
    digits = collapseDuplicateIndiaCodeInPhoneInput(digits);
  }

  let national: string;
  if (digits.startsWith("91")) {
    if (options?.countryCodeAlreadyPrefixed) {
      // react-phone-input-2 always prefixes 91 for India (12 digits = 91 + 10-digit national).
      if (digits.length < 12) {
        return "";
      }
      national = digits.slice(2);
    } else if (digits.length >= 12) {
      national = digits.slice(2);
    } else if (digits.length === 10) {
      national = digits;
    } else {
      return "";
    }
  } else if (digits.startsWith("0") && digits.length === 11) {
    national = digits.slice(1);
  } else if (digits.length === 10) {
    national = digits;
  } else {
    return "";
  }

  if (national.length === 10 && /^[6-9]\d{9}$/.test(national)) {
    return national;
  }
  return "";
}

/**
 * Extracts a 10-digit Indian national mobile from UI/API forms.
 * Accepts 10 digits, 91XXXXXXXXXX, +91XXXXXXXXXX, and optional spaces/dashes/trunk 0.
 * Returns "" when the value cannot be reduced to a valid 10-digit mobile (starts 6–9).
 */
export function extractIndianNationalNumber(phone?: string | null): string {
  const digits = String(phone ?? "").replace(/\D/g, "");
  return parseIndianNationalDigits(digits);
}

/**
 * Same as extractIndianNationalNumber but for react-phone-input-2 (India), where the
 * country code is always prefixed and partial entry must not pass as a full mobile.
 */
export function extractIndianNationalFromPhoneInput(
  phone?: string | null
): string {
  const digits = String(phone ?? "").replace(/\D/g, "");
  return parseIndianNationalDigits(digits, { countryCodeAlreadyPrefixed: true });
}

/** True when India phone picker value repeats the country code in the typed digits. */
export function hasDuplicateIndiaCodeInPhoneInput(phone?: string | null): boolean {
  const raw = String(phone ?? "").replace(/\D/g, "");
  return (
    raw.startsWith("91") &&
    raw.length === 12 &&
    raw.startsWith("9191") &&
    !raw.startsWith("91917")
  );
}

export function toE164India(phone?: string | null): string {
  const national = extractIndianNationalNumber(phone);
  return national ? `+91${national}` : "";
}

export function toE164IndiaFromPhoneInput(phone?: string | null): string {
  const digits = String(phone ?? "").replace(/\D/g, "");
  const national = extractIndianNationalFromPhoneInput(phone);
  if (!national) {
    return "";
  }
  // Picker value already includes country code 91 once — do not prepend another +91.
  if (digits.startsWith("91") && digits.length >= 12) {
    return `+${digits}`;
  }
  return `+91${national}`;
}

/**
 * Builds E.164 for non-India numbers from react-phone-input-2 values
 * (digits already include the calling code, usually without "+").
 * Dial-code-only values are treated as empty.
 */
export function toE164International(phone?: string | null): string {
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (!digits || digits.length <= 3) {
    return "";
  }
  return `+${digits}`;
}

export function validateCategory(category: string): boolean {
  return trimString(category) !== "";
}

/* -------------------------------------------------------------------------- */
/*                        Compare two string                                  */
/* -------------------------------------------------------------------------- */
export type CompareTwoStringResult = {
  isValid: boolean;
  message: string;
};
export function compareTwoString(
  str1: string,
  str2: string
): CompareTwoStringResult {
  str1 = trimString(str1);
  str2 = trimString(str2);

  if (str1 !== str2) {
    return { isValid: false, message: "String mismatch" };
  }

  return { isValid: true, message: "String matched" };
}
